import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CredenciaisApi, PermissoesCredencialApi, PlanosApi, TabelaPropriaApi } from '@/services/api-routes'
import type { Credencial } from '@/types/credencial'
import type { DefinirPermissoesPayload } from '@/types/lookup'
import { credenciaisKeys } from '@/http/queryKeys'
import { notifyErrorFor, notifySuccess } from '@/utility/notify'

export const useAtualizarCredencialMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credencial: Credencial) => CredenciaisApi.atualizar(credencial),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.all })
      notifySuccess('Credencial atualizada com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('atualizar a credencial', error),
  })
}

export const useAtualizarSituacaoCredencialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ credenciaisId, ativa }: { credenciaisId: string; ativa: boolean }) =>
      CredenciaisApi.atualizarSituacao(credenciaisId, ativa),
    onSuccess: (_data, { ativa }) => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.all })
      notifySuccess(`Credencial ${ativa ? 'ativada' : 'inativada'} com sucesso!`)
    },
    onError: (error) => notifyErrorFor('atualizar a situação da credencial', error),
  })
}

export const useDefinirTiposArquivosDoTipoMutation = (
  tipoCredencialId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tiposArquivosIds: string[]) =>
      CredenciaisApi.definirTiposArquivosDoTipo(tipoCredencialId, tiposArquivosIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.all })
      notifySuccess('Tipos de arquivo atualizados com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('atualizar os tipos de arquivo aceitos', error),
  })
}

export const useDefinirPermissoesMutation = (credenciaisId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: DefinirPermissoesPayload) =>
      PermissoesCredencialApi.definir(credenciaisId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.permissoes(credenciaisId) })
      notifySuccess('Permissões atualizadas com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('atualizar as permissões da credencial', error),
  })
}

export const useCriarPlanoMutation = (credenciaisId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: { descricao: string; codigo: string | null }) =>
      PlanosApi.criar(credenciaisId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.planosPagina(credenciaisId) })
      notifySuccess('Plano criado com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('criar o plano', error),
  })
}

export const useAtualizarPlanoMutation = (credenciaisId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body: { descricao: string; codigo: string | null }
    }) => PlanosApi.atualizar(credenciaisId, id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.planosPagina(credenciaisId) })
      notifySuccess('Plano atualizado com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('atualizar o plano', error),
  })
}

export const useExcluirPlanoMutation = (credenciaisId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (planoId: string) => PlanosApi.excluir(credenciaisId, planoId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: credenciaisKeys.planosPagina(credenciaisId) })
      notifySuccess('Plano excluído com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('excluir o plano', error),
  })
}

export const useImportarTabelaPropriaMutation = (credenciaisId: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ base64, name, type }: { base64: string; name: string; type: string }) =>
      TabelaPropriaApi.importar(credenciaisId, base64, name, type),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: credenciaisKeys.tabelaPropriaPagina(credenciaisId),
      })
      notifySuccess('Tabela de procedimentos importada com sucesso!')
      onSuccess?.()
    },
    onError: (error) => notifyErrorFor('importar a tabela de procedimentos', error),
  })
}

export const useAlterarSituacaoProcedimentoTabelaMutation = (credenciaisId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ativo, id }: { id: string; ativo: boolean }) =>
      TabelaPropriaApi.alterarSituacao(credenciaisId, id, ativo),
    onSuccess: (_data, { ativo }) => {
      void queryClient.invalidateQueries({
        queryKey: credenciaisKeys.tabelaPropriaPagina(credenciaisId),
      })
      notifySuccess(`Procedimento ${ativo ? 'ativado' : 'inativado'} com sucesso!`)
    },
    onError: (error) => notifyErrorFor('atualizar a situação do procedimento', error),
  })
}

export const useBaixarTabelaPropriaMutation = (credenciaisId: string, nomeArquivoPadrao: string) =>
  useMutation({
    mutationFn: () => TabelaPropriaApi.baixar(credenciaisId, nomeArquivoPadrao),
    onError: (error) => notifyErrorFor('baixar a tabela', error),
  })
